import type { MetricsOverviewDto, RevenueByPlanDto } from '@planuze/admin-contracts';
import { useMemo, useState } from 'react';
import { useMetricsWidgetsContent } from '../../../content/sections/metrics-widgets.js';
import type { Currency } from '../../../lib/format.js';
import { FacetMultiSelect } from '../../filters/facet-multi-select.js';
import { CustomersByPlanChart } from '../customers-by-plan-chart.js';
import { MetricCardsGrid } from '../metric-cards-grid.js';

interface Props {
  readonly overview: MetricsOverviewDto | undefined;
  readonly revenue: RevenueByPlanDto | undefined;
  readonly loading: boolean;
  readonly currency: Currency;
}

/* Aba Assinantes: cards de atividade/contas + barras de clientes por plano. Reusa a mesma
   query de revenue (queryKey deduplicada). Filtro de plano client-side sobre `items[].plan`. */
export function SubscribersTab({ overview, revenue, loading, currency }: Props) {
  const copy = useMetricsWidgetsContent().planFilter;
  const [plans, setPlans] = useState<string[]>([]);
  const items = revenue?.items ?? [];
  const planOptions = useMemo(
    () => items.map((item) => ({ value: item.plan, label: item.plan })),
    [items],
  );
  const filtered = useMemo(
    () => (plans.length === 0 ? items : items.filter((item) => plans.includes(item.plan))),
    [items, plans],
  );
  const clearFilters = (): void => setPlans([]);
  const filteredData = revenue === undefined ? undefined : { ...revenue, items: filtered };

  return (
    <div className="space-y-4">
      <MetricCardsGrid overview={overview} section="subscribers" />
      <div className="flex flex-wrap items-center gap-3">
        <FacetMultiSelect
          label={copy.label}
          values={plans}
          onChange={setPlans}
          options={planOptions}
        />
        {plans.length > 0 && (
          <span className="text-sm text-muted-foreground">
            {copy.resultCount(filtered.length, items.length)}
          </span>
        )}
      </div>
      <CustomersByPlanChart
        data={filteredData}
        loading={loading}
        currency={currency}
        hasFilters={plans.length > 0}
        onClearFilters={clearFilters}
      />
    </div>
  );
}
